import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import * as Sentry from '@sentry/react';
import { playerApi, API_BASE } from '../../lib/api';
import { usePersistedState } from '../../lib/usePersistedState';
import TagSelector from '../../components/TagSelector';
import LanguageSelector from '../../components/LanguageSelector';
import SettingsModal from '../../components/SettingsModal';
import PresetSelector from '../../components/PresetSelector';
import { useToast } from '../../components/Toast';
import { useTrialMode } from './trial/useTrialMode';
import TrialEndModal from './trial/TrialEndModal';
import { useVisibilityTelemetry } from './useVisibilityTelemetry';

// Approximate seconds of new audio each track contributes to the stream.
// durationTrack(180) - crossfadeDuration(15) = 165s
const TRACK_INTERVAL = 165;
const MAX_TRACKS = 20;
const PRE_GEN_TRACK_LENGTH = 60;
const PRE_GEN_CROSSFADE = 15;

// ── Playback-reliability tunables (ENG-5298) ───────────────────────────────
// A stall (audio 'waiting'/'stalled', or hls.js bufferStalledError) is audible
// silence for store BGM, so we surface it FAST even though it may self-recover:
//  - STALL_WARN_MS: past this it's clearly a stall (not a sub-second segment
//    blip) → emit a `playback degraded: stall` warning so every silence event
//    is counted, even if it recovers a moment later.
//  - STALL_TICK_MS: how often the watchdog re-checks + nudges (startLoad+play).
//  - STALL_ESCALATE_MS: continuous silence past this → manual-restart overlay.
//  - STALL_STARTUP_ESCALATE_MS: a stall before playback ever started (currentTime
//    still 0 — e.g. the first post-init live.m3u8 had no loadable segments yet,
//    an init→playlist readiness race) is recoverable once the playlist reloads,
//    so give it a longer grace + force reloads before the overlay.
const STALL_WARN_MS = 3_000;
const STALL_TICK_MS = 2_000;
const STALL_ESCALATE_MS = 12_000;
const STALL_STARTUP_ESCALATE_MS = 30_000;
// On a FATAL hls.js network error (e.g. live.m3u8 5xx from a D1 overload spike,
// or a transient WiFi drop) try a bounded number of in-place reloads before
// giving up to the overlay.
const HLS_MAX_NETWORK_RETRIES = 3;
const HLS_RETRY_DELAY_MS = 2_000;
// Hard ceiling on how long we poll a single SOUNDRAW generation before giving
// up. Without this the player polls /status forever and the UI sits on the
// "生成中" spinner indefinitely when SOUNDRAW (or D1) is degraded.
// Split by path: startup has no buffered audio, so a long wait is silent dead
// air — keep it tight. Steady-state extends run behind ~180s of buffered
// headroom, so they can afford to sit above the ffmpeg-worker's 150s
// healthy-extend bar (~15s under the 180s buffer drain) instead of firing
// prematurely on an encode the buffer would have absorbed.
const GENERATE_TIMEOUT_STARTUP_MS = 90_000;
// 165_000ms == TRACK_INTERVAL (165s), the net audio one extend adds. Capping the
// poll budget at the playback an extend buys back means a slower-than-realtime
// encode times out instead of silently draining the buffer it was meant to refill.
const GENERATE_TIMEOUT_EXTEND_MS = 165_000;
// Sample buffer depth this often as a Sentry breadcrumb so a later stop carries
// the buffer trajectory that led to it.
const BUFFER_SAMPLE_MS = 30_000;

// ── Auto-recovery watchdog (ENG-5468) ──────────────────────────────────────
// Unattended store kiosks sit on slow/flaky networks (千種店 ≈ 0.5 Mbps). Once
// the bounded hls.js recovery above (HLS_MAX_NETWORK_RETRIES + ~24s reconnect,
// PR #167) gives up, the player lands in the terminal "manual-restart overlay"
// state (unexpectedlyStopped) and just sits dead — and nobody is at the kiosk
// to tap it. This watchdog layers ON TOP of the existing recovery (it does NOT
// change the bounded hls retries or the 401/session-replace/expire path) and
// escalates in two tiers:
//   Tier 1 — bounded in-place re-init (preferred, non-disruptive): reuse the
//            existing reclaim+restart path (fresh session claim → full teardown
//            → re-poll → re-create hls → re-attach) WITHOUT a page reload.
//   Tier 2 — a single gated window.location.reload() as a last resort, only
//            after Tier-1 is exhausted AND the player has been continuously dead
//            for a long window, guarded so a permanently-dead network can't
//            reload-cycle.
// Flip WATCHDOG_ENABLED to false to disable the whole watchdog fast.
const WATCHDOG_ENABLED = true;
// How often the watchdog re-evaluates the player's health.
const WATCHDOG_TICK_MS = 15_000;
// Grace before the first Tier-1 re-init (lets the existing in-place recovery /
// a late extend land first) and minimum backoff between subsequent attempts.
const WATCHDOG_REINIT_BACKOFF_MS = 20_000;
// Tier-1 in-place re-init attempts before escalating to Tier-2.
const WATCHDOG_MAX_REINIT = 3;
// Continuous dead time (Tier-1 exhausted) before a Tier-2 full reload.
const WATCHDOG_RELOAD_AFTER_MS = 180_000; // ~3 min
// Loop guard: at most ~1 full reload per this window. A reload within cooldown
// is suppressed (we keep trying Tier-1 in place) so a permanently-dead network
// can't reload-cycle. Persisted across the reload it guards.
const WATCHDOG_RELOAD_COOLDOWN_MS = 600_000; // ~10 min
// Sustained healthy playback that counts as a "real recovery" — after this the
// reload loop-guard is cleared so normal operation isn't penalised by an old
// reload marker.
const WATCHDOG_HEALTHY_RESET_MS = 60_000;
// localStorage key prefix (per token) for the cross-reload loop guard.
const WATCHDOG_RELOAD_KEY_PREFIX = 'spacemusic.watchdog.reload.';

// Classify a claim/session failure's HTTP status as transient (should be
// retried, not treated as a terminal/expired session). 408 (Request Timeout)
// and 429 (Too Many Requests) are 4xx but TRANSIENT — on a slow/rate-limited
// store network they must recover via the watchdog, not permanently stand down.
// A pure network throw (no status) and any 5xx are transient too. Everything
// else in the 4xx range (401/403/404/…) is genuinely terminal.
const isTransientStatus = (status: number | undefined): boolean =>
  status === undefined || status >= 500 || status === 408 || status === 429;

type WatchdogReloadMark = { ts: number; count: number };

// Read the persisted reload marker (survives window.location.reload()). Any
// storage error / malformed value is treated as "no prior reload".
const readReloadMark = (key: string): WatchdogReloadMark => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WatchdogReloadMark>;
      return { ts: Number(parsed.ts) || 0, count: Number(parsed.count) || 0 };
    }
  } catch {
    // localStorage unavailable / quota / malformed — no guard state to honour
  }
  return { ts: 0, count: 0 };
};

const writeReloadMark = (key: string, mark: WatchdogReloadMark) => {
  try {
    localStorage.setItem(key, JSON.stringify(mark));
  } catch {
    // ignore — worst case the loop guard is missing for this one reload
  }
};

const clearReloadMark = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const getTagKey = (mood: string | null, genres: string[], isMelody: boolean) =>
  `${mood ?? ''}|${[...genres].sort().join(',')}|${isMelody ? 'melody' : 'inst'}`;

// Seconds of audio buffered ahead of the current playhead (0 if unknown).
const getBufferAhead = (audio: HTMLAudioElement): number => {
  try {
    const b = audio.buffered;
    for (let i = 0; i < b.length; i++) {
      if (audio.currentTime >= b.start(i) && audio.currentTime <= b.end(i)) {
        return Math.max(0, b.end(i) - audio.currentTime);
      }
    }
  } catch {
    // buffered can throw before the media element is ready
  }
  return 0;
};

interface Track {
  id: string;
  url: string;
  mood: string | null;
  genres: string[];
}

export default function Player() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();

  // Store / session
  const [storeName, setStoreName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invalidToken, setInvalidToken] = useState(false);

  // Tags
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [allMoods, setAllMoods] = useState<string[]>([]);
  const [allGenres, setAllGenres] = useState<string[]>([]);
  const [availableMoods, setAvailableMoods] = useState<string[] | undefined>(undefined);
  const [availableGenres, setAvailableGenres] = useState<string[] | undefined>(undefined);

  // Playback
  const [tracks, setTracks] = useState<Track[]>([]);
  const [phase, setPhase] = useState<'idle' | 'starting' | 'playing' | 'stopped'>('idle');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPipelineRunning, setIsPipelineRunning] = useState(false);
  const [unexpectedlyStopped, setUnexpectedlyStopped] = useState(false);
  // Browser autoplay policy blocked a gesture-less audio.play() (NotAllowedError).
  // Kept SEPARATE from unexpectedlyStopped: an autoplay block is not a network
  // outage and can only be cleared by a real user gesture, so it must NOT arm any
  // auto-recovery path — it only surfaces the same tap-to-resume overlay.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);

  // Progress
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Crossfade — persisted per-device so users don't re-configure each session
  const [crossfadeEnabled, setCrossfadeEnabled] = usePersistedState(
    'spacemusic.player.crossfade',
    false,
  );

  const [melody, setMelody] = usePersistedState('spacemusic.player.melody', false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const handleSettingsClose = useCallback(() => setSettingsOpen(false), []);

  // Volume
  // iOS Safari ignores audio.volume (read-only, hardware-controlled).
  // On iOS we show a mute toggle instead of the slider.
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const hlsAudioRef = useRef<HTMLAudioElement>(null);
  const trial = useTrialMode(hlsAudioRef, !loading);
  const hlsInstanceRef = useRef<Hls | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pipelineRunIdRef = useRef(0);
  const tracksRef = useRef<Track[]>([]);
  const volumeRef = useRef(0.8);
  const isPlayingRef = useRef(false);
  const isIntentionallyPausedRef = useRef(false);
  const unexpectedlyStoppedRef = useRef(false);
  const autoplayBlockedRef = useRef(false);
  const tokenRef = useRef(token);
  const tryResumeOrRestartRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const selectedMoodRef = useRef<string | null>(null);
  const selectedGenresRef = useRef<string[]>([]);
  const isFirstRender = useRef(true);

  // Pre-generation refs
  const preGenRunIdRef = useRef(0);
  const preGenPromiseRef = useRef<Promise<[Track, Track]> | null>(null);
  const preGenTagKeyRef = useRef<string>('');
  // Track length used for pre-generated tracks (shorter = faster init, then pipeline uses 180s)
  const preGenTrackLengthRef = useRef<number>(180);
  // Crossfade to use for the first extend after init (matches init's crossfade for seamless seam)
  const firstExtendCrossfadeRef = useRef<number>(15);

  const crossfadeEnabledRef = useRef(false);
  const melodyRef = useRef(false);
  const hlsPollAbortRef = useRef<AbortController | null>(null);

  // Playback-reliability refs (ENG-5298)
  const phaseRef = useRef<'idle' | 'starting' | 'playing' | 'stopped'>('idle');
  const stallStartRef = useRef<number | null>(null);
  const stallWarnedRef = useRef(false); // warned-once-per-stall guard
  const stallWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const beginStallRef = useRef<() => void>(() => {}); // bridge: hls.js bufferStalledError → stall watchdog
  const hlsRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // pending HLS auto-recovery
  const hlsRecoveryRef = useRef(0); // consecutive in-place HLS recovery attempts
  const pipelineFailRef = useRef(0); // consecutive extend-pipeline failures
  const engineLoggedRef = useRef(false); // emit the engine (hls.js/native) once per session
  const hasPlayedRef = useRef(false); // playback has advanced past 0 this session (startup vs mid-stream stall)

  // Auto-recovery watchdog refs (ENG-5468). Latched engagement so the watchdog
  // keeps driving recovery across the transient phase flips its own re-inits
  // cause, until it confirms sustained healthy playback.
  const watchdogEngagedRef = useRef(false);
  const watchdogDeadSinceRef = useRef<number | null>(null); // start of the current dead episode
  const watchdogReinitRef = useRef(0); // Tier-1 re-init attempts this episode
  const watchdogLastAttemptRef = useRef(0); // timestamp of the last Tier-1 attempt (grace/backoff)
  const watchdogHealthySinceRef = useRef<number | null>(null); // start of sustained healthy playback
  const watchdogAutoStartedRef = useRef(false); // one-shot auto-start after a Tier-2 reload
  const reclaimAndRestartRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false));
  const startSessionRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Refs mirroring state the watchdog reads on its ticks without re-arming.
  const sessionExpiredRef = useRef(false);
  const restartingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);
  useEffect(() => {
    crossfadeEnabledRef.current = crossfadeEnabled;
  }, [crossfadeEnabled]);
  useEffect(() => {
    melodyRef.current = melody;
  }, [melody]);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    selectedMoodRef.current = selectedMood;
  }, [selectedMood]);
  useEffect(() => {
    selectedGenresRef.current = selectedGenres;
  }, [selectedGenres]);
  useEffect(() => {
    unexpectedlyStoppedRef.current = unexpectedlyStopped;
  }, [unexpectedlyStopped]);
  useEffect(() => {
    autoplayBlockedRef.current = autoplayBlocked;
  }, [autoplayBlocked]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    sessionExpiredRef.current = sessionExpired;
  }, [sessionExpired]);
  useEffect(() => {
    restartingRef.current = restarting;
  }, [restarting]);
  // Tag every Sentry event from this session with the store name so engine /
  // stall / stop events are readable per store without a token→D1 lookup.
  useEffect(() => {
    if (storeName) Sentry.setTag('store', storeName);
  }, [storeName]);

  // TELEMETRY ONLY: record whether this store's player stays foregrounded on
  // its iPad (WebKit throttles/suspends background tabs). Self-contained — does
  // not affect playback, retry, or stall logic. Events carry the store tag.
  useVisibilityTelemetry(storeName);

  // ── Session-replaced cleanup ──────────────────────────────────────────────
  // Stops all stale activity (pipeline, pre-gen, HLS, heartbeat, audio) then
  // surfaces the session-replaced overlay. Called from both the heartbeat
  // eviction path and the HLS 401 handler so neither path leaves background
  // work running.
  const stopAndExpire = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    pipelineRunIdRef.current++;
    preGenRunIdRef.current++;
    hlsPollAbortRef.current?.abort();
    hlsPollAbortRef.current = null;
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.detachMedia();
      hlsInstanceRef.current.removeAllListeners();
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    const audioEl = hlsAudioRef.current;
    if (audioEl) {
      audioEl.pause();
      audioEl.src = '';
    }
    if (stallWatchdogRef.current) {
      clearTimeout(stallWatchdogRef.current);
      stallWatchdogRef.current = null;
    }
    stallStartRef.current = null;
    if (hlsRetryTimerRef.current) {
      clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }
    setSessionExpired(true);
  }, []);

  // ── Playback-stop alerting (ENG-5298) ─────────────────────────────────────
  // Single funnel for every way playback can die on a store's screen. All
  // events share the "playback stopped: <reason>" message + a per-reason
  // fingerprint, so one Sentry alert rule (message starts with
  // "playback stopped:") pages on real outages, grouped by cause. Buffer
  // breadcrumbs collected elsewhere ride along on each event.
  const capturePlaybackStop = useCallback(
    (reason: string, extra?: Record<string, unknown>) => {
      const audio = hlsAudioRef.current;
      Sentry.captureMessage(`playback stopped: ${reason}`, {
        level: 'error',
        fingerprint: ['playback stopped', reason],
        tags: {
          playbackStopReason: reason,
          // Last 6 chars only — correlate stores without leaking the token.
          tokenSuffix: token?.slice(-6) ?? '',
        },
        extra: {
          bufferAhead: audio ? getBufferAhead(audio) : null,
          currentTime: audio?.currentTime ?? null,
          ...extra,
        },
      });
    },
    [token],
  );

  // Warning-level "playback degraded: <reason>" — a problem occurred that may
  // self-recover (a stall = audible silence; a flaky extend). Separate family
  // from the error-level "playback stopped:" so it can have its own alert rule.
  const capturePlaybackDegraded = useCallback(
    (reason: string, extra?: Record<string, unknown>) => {
      Sentry.captureMessage(`playback degraded: ${reason}`, {
        level: 'warning',
        fingerprint: ['playback degraded', reason],
        tags: { playbackDegradeReason: reason, tokenSuffix: token?.slice(-6) ?? '' },
        extra,
      });
    },
    [token],
  );

  // Clear any pending stall watchdog + reset stall bookkeeping.
  const clearStall = useCallback(() => {
    if (stallWatchdogRef.current) {
      clearTimeout(stallWatchdogRef.current);
      stallWatchdogRef.current = null;
    }
    stallStartRef.current = null;
    stallWarnedRef.current = false;
  }, []);

  // Persistent extend-pipeline failures don't stop audio immediately (the player
  // buffer absorbs them) but predict an imminent stop. Alert once per streak.
  const notePipelineFailure = useCallback(
    (reason: string, extra?: Record<string, unknown>) => {
      pipelineFailRef.current++;
      if (pipelineFailRef.current === 3) {
        capturePlaybackDegraded(reason, { consecutiveFailures: pipelineFailRef.current, ...extra });
      }
    },
    [capturePlaybackDegraded],
  );

  // ── Init: load store info and claim session ───────────────────────────────
  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const storeList = await playerApi.getStores(token);
        if (!storeList[0]) {
          setInvalidToken(true);
          return;
        }
        setStoreName(storeList[0].name);
        setLogoUrl(storeList[0].logoUrl);
        const session = await playerApi.claimSession(token);
        sessionTokenRef.current = session.sessionToken;
        heartbeatRef.current = setInterval(async () => {
          try {
            const result = await playerApi.heartbeat(token, sessionTokenRef.current!);
            if (result.evicted) stopAndExpire();
          } catch {
            // network hiccup — keep retrying
          }
        }, session.heartbeatInterval * 1000);
      } catch (e) {
        // Post-reload init can fail on a degraded kiosk network. Distinguish a
        // terminal bad token from a transient outage by the HTTP status
        // attached in handleResponse; a pure network throw carries no `.status`.
        const status = (e as { status?: number } | undefined)?.status;
        if (!isTransientStatus(status)) {
          // Genuine bad token / single-session rejection (terminal 4xx:
          // 401/403/404/…) — terminal. Must NOT auto-recover, or this device
          // would reload-war with the one that actually holds the single
          // session. Transient 4xx (408/429) fall through to recovery below.
          setInvalidToken(true);
        } else {
          // 5xx, a network throw (no status), or a transient 4xx (408/429) →
          // transient. Skip the terminal page and latch the state the watchdog
          // engages on, so it drives reclaimAndRestart (which claims a fresh
          // session itself) on its backoff until the network recovers.
          Sentry.captureMessage('watchdog: init claim failed (transient), engaging recovery', {
            level: 'warning',
            tags: { tokenSuffix: token?.slice(-6) ?? '' },
            extra: { status: status ?? null },
          });
          setUnexpectedlyStopped(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token, stopAndExpire]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    const runIdRef = pipelineRunIdRef;
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      runIdRef.current++;
      hlsPollAbortRef.current?.abort();
      hlsPollAbortRef.current = null;
      if (hlsRetryTimerRef.current) {
        clearTimeout(hlsRetryTimerRef.current);
        hlsRetryTimerRef.current = null;
      }
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.detachMedia();
        hlsInstanceRef.current.removeAllListeners();
        hlsInstanceRef.current.destroy();
      }
      if (token && sessionTokenRef.current) {
        void playerApi.release(token, sessionTokenRef.current);
      }
    };
  }, [token]);

  // ── Resume after network reconnection ────────────────────────────────────
  useEffect(() => {
    const onOnline = () => {
      if (unexpectedlyStoppedRef.current) {
        void tryResumeOrRestartRef.current();
        return;
      }
      const audio = hlsAudioRef.current;
      if (audio && audio.paused && isPlayingRef.current && audio.src) {
        void audio.play().catch(() => null);
      }
    };
    const tryResume = () => {
      // Polling only handles simple pauses, not fatal HLS errors
      // (fatal recovery is triggered by the 'online' event or the manual button)
      if (unexpectedlyStoppedRef.current) return;
      const audio = hlsAudioRef.current;
      if (audio && audio.paused && isPlayingRef.current && audio.src) {
        void audio.play().catch(() => null);
      }
    };
    // iOS Safari freezes JS timers while the iPad is locked/backgrounded. Use
    // visibilitychange (which fires *before* the freeze on hide, and on return)
    // to keep the time-based stall watchdog honest:
    //  - on hide: drop stall tracking so the frozen watchdog can't escalate to a
    //    false "tap to restart" using stale wall-clock elapsed when we resume.
    //  - on show: clear again and nudge a paused stream back to life.
    const onVisibility = () => {
      clearStall();
      if (document.hidden) return;
      if (unexpectedlyStoppedRef.current) {
        void tryResumeOrRestartRef.current();
        return;
      }
      const audio = hlsAudioRef.current;
      if (audio && audio.paused && isPlayingRef.current && audio.src) {
        hlsInstanceRef.current?.startLoad();
        void audio.play().catch(() => null);
      }
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(tryResume, 5000);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [clearStall]);

  // ── Tags ──────────────────────────────────────────────────────────────────
  const fetchAvailableTags = useCallback(
    async (moods: string[], genres: string[]) => {
      if (!token) return;
      try {
        const result = await playerApi.getTags(token, { moods, genres });
        setAvailableMoods(result.moods);
        setAvailableGenres(result.genres);
      } catch {
        setAvailableMoods(undefined);
        setAvailableGenres(undefined);
      }
    },
    [token],
  );

  useEffect(() => {
    if (loading || invalidToken) return;
    void (async () => {
      if (!token) return;
      try {
        const result = await playerApi.getTags(token, { moods: [], genres: [] });
        setAllMoods(result.moods);
        setAllGenres(result.genres);
        setAvailableMoods(result.moods);
        setAvailableGenres(result.genres);
      } catch {
        // leave empty
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (loading || invalidToken) return;
    void fetchAvailableTags(selectedMood ? [selectedMood] : [], selectedGenres);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMood, selectedGenres]);

  // ── Core helpers ──────────────────────────────────────────────────────────

  // Generate one track and poll until complete.
  // isCancelled is called before each poll; if it returns true the promise rejects.
  // length defaults to 180s for pipeline tracks; use a shorter value for faster startup.
  const generateTrack = useCallback(
    // timeoutSeverity controls both the poll budget (extends get the longer
    // GENERATE_TIMEOUT_EXTEND_MS) and what a generation timeout reports:
    //  'stop'     — startup generation (startSession): the session fails to start
    //               → capturePlaybackStop (error/page).
    //  'degraded' — extend pipeline: the buffer covers it and we retry, so it's a
    //               warning, not a stop — but still recorded PER OCCURRENCE with
    //               the task id, so the signal + root cause aren't lost when a
    //               near-tail buffer later drains into a stall (Greptile, PR #170).
    //  'none'     — idle pre-generation: rejection swallowed, nothing affected.
    (
      isCancelled: () => boolean,
      length = 180,
      timeoutSeverity: 'stop' | 'degraded' | 'none' = 'stop',
    ): Promise<Track> => {
      if (!token || !sessionTokenRef.current) return Promise.reject(new Error('No session'));
      const requestedMood = selectedMoodRef.current;
      const requestedGenres = [...selectedGenresRef.current];
      return new Promise((resolve, reject) => {
        playerApi
          .generate(token, sessionTokenRef.current!, {
            moods: requestedMood ? [requestedMood] : [],
            genres: requestedGenres,
            themes: [],
            length,
            muteStems: !melodyRef.current ? ['me'] : undefined,
          })
          .then((res) => {
            let startedAt = Date.now();
            let lastTick = Date.now();
            // Steady-state extends ('degraded') sit behind ~180s of buffered
            // headroom, so they get the longer budget; startup ('stop') and idle
            // ('none') have no buffer to cover the wait, so they stay tight.
            const timeoutMs =
              timeoutSeverity === 'degraded'
                ? GENERATE_TIMEOUT_EXTEND_MS
                : GENERATE_TIMEOUT_STARTUP_MS;
            const poll = () => {
              if (isCancelled()) {
                reject(new Error('Pipeline cancelled'));
                return;
              }
              const now = Date.now();
              // iOS Safari freezes JS timers while the tab is backgrounded / the
              // iPad is locked. A large jump between ticks means we were
              // suspended, not that generation is genuinely slow — push the
              // deadline out by the suspended gap so we don't falsely time out
              // (and falsely alert) the moment the screen comes back.
              const gap = now - lastTick;
              if (gap > 30_000) startedAt += gap;
              lastTick = now;
              if (now - startedAt > timeoutMs) {
                // SOUNDRAW never reached 'done' within budget. Give up instead of
                // polling forever, so the UI stops spinning on 生成中 indefinitely
                // (ENG-5298 problem A). startSession surfaces the error toast.
                const timeoutExtra = { taskId: res.taskId, elapsedMs: now - startedAt };
                if (timeoutSeverity === 'stop') {
                  capturePlaybackStop('generation-timeout', timeoutExtra);
                } else if (timeoutSeverity === 'degraded') {
                  capturePlaybackDegraded('generation-timeout', timeoutExtra);
                }
                reject(new Error('Track generation timed out'));
                return;
              }
              playerApi
                .getStatus(token, res.taskId)
                .then((data) => {
                  if (isCancelled()) {
                    reject(new Error('Pipeline cancelled'));
                    return;
                  }
                  if (data.status === 'completed' && data.trackUrl) {
                    resolve({
                      id: res.taskId,
                      url: data.trackUrl,
                      mood: requestedMood,
                      genres: requestedGenres,
                    });
                  } else if (data.status === 'error') {
                    reject(new Error('Track generation failed'));
                  } else {
                    setTimeout(poll, 1000);
                  }
                })
                .catch(() => {
                  // Transient /status failure (D1 overload 5xx, WiFi blip). One
                  // hiccup must not abort the whole generation — keep polling
                  // until the deadline above trips.
                  if (isCancelled()) {
                    reject(new Error('Pipeline cancelled'));
                    return;
                  }
                  setTimeout(poll, 1000);
                });
            };
            setTimeout(poll, 1000);
          })
          .catch(reject);
      });
    },
    [token, capturePlaybackStop, capturePlaybackDegraded],
  );

  // Pre-generate 2 tracks in the background for the current tag selection.
  // Uses its own run ID so it can be cancelled independently of the main pipeline.
  // Uses a short track length so SOUNDRAW responds faster; the crossfade duration is
  // adjusted accordingly to keep seams seamless.
  const preGenerateTracks = useCallback(() => {
    if (!token || !sessionTokenRef.current) return;
    preGenRunIdRef.current++;
    const preRunId = preGenRunIdRef.current;
    preGenTagKeyRef.current = getTagKey(
      selectedMoodRef.current,
      selectedGenresRef.current,
      melodyRef.current,
    );
    preGenTrackLengthRef.current = PRE_GEN_TRACK_LENGTH;
    const isCancelled = () => preGenRunIdRef.current !== preRunId;
    preGenPromiseRef.current = Promise.all([
      generateTrack(isCancelled, PRE_GEN_TRACK_LENGTH, 'none'),
      generateTrack(isCancelled, PRE_GEN_TRACK_LENGTH, 'none'),
    ]);
    // Suppress unhandled rejection if pre-gen is cancelled
    preGenPromiseRef.current.catch(() => null);
  }, [token, generateTrack]);

  // Poll an HLS job until completed or failed.
  // Stops scheduling further polls when pipelineRunIdRef no longer matches runId.
  // Uses AbortController to cancel in-flight fetches on cancellation.
  const pollHlsJob = useCallback(
    (jobId: string, runId: number): Promise<void> => {
      if (!token) return Promise.reject(new Error('No token'));
      const abort = new AbortController();
      hlsPollAbortRef.current?.abort();
      hlsPollAbortRef.current = abort;
      return new Promise((resolve, reject) => {
        const check = () => {
          if (pipelineRunIdRef.current !== runId) {
            abort.abort();
            reject(new Error('Pipeline cancelled'));
            return;
          }
          fetch(`${API_BASE}/api/play/${token}/hls/status/${jobId}`, {
            headers: { 'X-Session-Token': sessionTokenRef.current ?? '' },
            signal: abort.signal,
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data: { status: string; error?: string; errorCode?: string }) => {
              if (pipelineRunIdRef.current !== runId) {
                abort.abort();
                reject(new Error('Pipeline cancelled'));
                return;
              }
              if (data.status === 'completed') resolve();
              else if (data.status === 'failed') {
                const err = Object.assign(new Error(data.error ?? 'HLS job failed'), {
                  errorCode: data.errorCode,
                });
                reject(err);
              } else setTimeout(check, 1000);
            })
            .catch(() => {
              if (abort.signal.aborted) {
                reject(new Error('Pipeline cancelled'));
                return;
              }
              setTimeout(check, 5000);
            });
        };
        check();
      });
    },
    [token],
  );

  // Attach hls.js (or native Safari HLS) to the audio element and start playback
  const loadHLS = useCallback(() => {
    const audio = hlsAudioRef.current;
    if (!audio || !token) return;
    const hlsUrl = `${API_BASE}/api/play/${token}/live.m3u8?sid=${sessionTokenRef.current}&t=${pipelineRunIdRef.current}`;

    // Record which HLS engine this device uses (hls.js MSE vs Safari native) —
    // a Sentry tag on every event + one info event per session for the
    // distribution. The `store` tag rides along, so we get hls.js-vs-native per
    // store by name. Matters because the two paths behave very differently in
    // the background (ENG-5307).
    const reportEngine = (engine: 'hlsjs' | 'native' | 'unsupported') => {
      Sentry.setTag('hlsEngine', engine);
      if (!engineLoggedRef.current) {
        engineLoggedRef.current = true;
        Sentry.captureMessage(`player engine: ${engine}`, {
          level: 'info',
          tags: { hlsEngine: engine },
        });
      }
    };

    // Handle a rejected programmatic play(). A NotAllowedError means the browser's
    // autoplay policy blocked a gesture-less start (the stream itself is fine) —
    // surface the tap-to-resume overlay via a DEDICATED flag and record an honest
    // 'stopped' phase. We deliberately do NOT set unexpectedlyStopped here: only a
    // real user gesture can lift an autoplay block, so any auto-recovery would just
    // re-hit the block (burning ffmpeg on futile re-inits). Anything else is a
    // genuine error → log it.
    const onPlayError = (err: unknown) => {
      if ((err as { name?: string } | undefined)?.name === 'NotAllowedError') {
        autoplayBlockedRef.current = true;
        setAutoplayBlocked(true);
        setPhase('stopped');
        return;
      }
      console.error(err);
    };

    if (Hls.isSupported()) {
      reportEngine('hlsjs');
      const prev = hlsInstanceRef.current;
      if (prev) {
        // Cancel a pending auto-recovery aimed at the instance we're replacing.
        if (hlsRetryTimerRef.current) {
          clearTimeout(hlsRetryTimerRef.current);
          hlsRetryTimerRef.current = null;
        }
        prev.detachMedia();
        prev.removeAllListeners();
        prev.destroy();
      }
      const hls = new Hls({
        lowLatencyMode: false,
        startPosition: 0,
        // Sliding-window playlist: never seek to the live edge — play linearly.
        liveSyncDuration: 999998,
        liveMaxLatencyDuration: 999999,
        // Evict played segments from the SourceBuffer promptly.
        backBufferLength: 0,
        // Cap forward buffer to match the ~20-min lookahead (ADR-007 Stage 1a).
        // At 96 kbps a ~25 min buffer ≈ ~18 MB, comfortably under hls.js's 60 MB
        // maxBufferSize default; backBufferLength:0 still evicts played audio so
        // memory stays bounded on weak devices (old Android/iPad).
        maxBufferLength: 1200,
        maxMaxBufferLength: 1500,
        // Pin the byte cap explicitly (hls.js default) so it stays self-documenting
        // and doesn't silently become the binding limit below the 1200s target if the
        // generated bitrate is ever raised above ~384 kbps.
        maxBufferSize: 60 * 1000 * 1000,
      });
      hlsInstanceRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(audio);
      hls.once(Hls.Events.MANIFEST_PARSED, () => {
        audio.volume = volumeRef.current;
        if (!isIntentionallyPausedRef.current) {
          void audio.play().catch(onPlayError);
        }
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) {
          // hls.js's own "playing but nothing buffered" signal — fires at the
          // live edge when an extend hasn't published the next segment yet
          // (exactly the 4:09/4:10 tail case). Route it into the stall watchdog
          // since the media 'waiting' event isn't guaranteed at the edge.
          if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            beginStallRef.current();
          }
          return;
        }
        // HTTP status of the failed playlist/segment fetch (0/none if no response).
        const hlsHttpCode = String(data.response?.code ?? 'none');
        const status = data.response?.code ?? data.networkDetails?.status;
        // A 401 on live.m3u8 means another client claimed this store's single
        // session (validateSession in player.ts), so our sessionToken is now
        // stale. This is expected single-session enforcement, not an outage —
        // show the same "session replaced" overlay as heartbeat eviction and
        // report it as a warning so it doesn't page like a real fatal (ENG-5138).
        if (status === 401) {
          console.warn('[HLS] session replaced (401) — playlist load rejected');
          Sentry.captureMessage('HLS session replaced (401)', {
            level: 'warning',
            tags: {
              hlsType: data.type,
              hlsDetails: data.details,
              // Last 6 chars only — correlate stores without leaking the token.
              tokenSuffix: token?.slice(-6) ?? '',
            },
            extra: { url: data.url?.split('?')[0], sid: sessionTokenRef.current?.slice(-6) },
          });
          stopAndExpire();
          return;
        }
        // A fatal error does NOT always mean the stream is dead. Network errors
        // (live.m3u8 5xx during a D1 overload spike, a transient WiFi drop) and
        // media errors are usually recoverable in place. Try a bounded number of
        // reloads before showing the store the manual-restart overlay — the main
        // lever against the "BGM keeps cutting out, staff must press play each
        // time" complaint (ENG-5298 problem B). Each attempt drops a breadcrumb
        // so a final give-up event carries the recovery history.
        const recoverable =
          data.type === Hls.ErrorTypes.NETWORK_ERROR || data.type === Hls.ErrorTypes.MEDIA_ERROR;
        if (recoverable && hlsRecoveryRef.current < HLS_MAX_NETWORK_RETRIES) {
          hlsRecoveryRef.current++;
          console.warn(
            `[HLS] fatal ${data.type}/${data.details} — auto-recovery ${hlsRecoveryRef.current}/${HLS_MAX_NETWORK_RETRIES}`,
          );
          Sentry.addBreadcrumb({
            category: 'playback',
            level: 'warning',
            message: `hls auto-recovery attempt ${hlsRecoveryRef.current}`,
            data: { type: data.type, details: data.details, httpCode: hlsHttpCode },
          });
          // Capture the instance we're scheduling for; if the session is torn
          // down or replaced (startSession/stopAndExpire) before the timer fires,
          // hlsInstanceRef no longer points at it and we must not touch the new
          // one. Track the timer so teardown can cancel it outright.
          const scheduledHls = hls;
          if (hlsRetryTimerRef.current) clearTimeout(hlsRetryTimerRef.current);
          hlsRetryTimerRef.current = setTimeout(() => {
            hlsRetryTimerRef.current = null;
            if (hlsInstanceRef.current !== scheduledHls || unexpectedlyStoppedRef.current) return;
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR) scheduledHls.recoverMediaError();
            else scheduledHls.startLoad();
            void hlsAudioRef.current?.play().catch(() => null);
          }, HLS_RETRY_DELAY_MS);
          return;
        }
        // Out of retries (or a non-recoverable error) — surface the overlay and
        // alert. Routed through capturePlaybackStop so it joins the single
        // "playback stopped: <reason>" alert family; hlsHttpCode keeps the
        // failure modes distinguishable (0/none = connection dropped; 5xx =
        // backend; 401 = session replaced, handled above as a warning).
        console.error('[HLS] fatal error (unrecovered)', data);
        capturePlaybackStop('hls-fatal', {
          hlsType: data.type,
          hlsDetails: data.details,
          hlsHttpCode,
          httpCode: data.response?.code,
          url: data.url?.split('?')[0],
          // hls.js embeds the playlist URL (with ?sid=<sessionToken>) in the
          // error message — strip any query string so the credential is not
          // forwarded to Sentry.
          reason: data.error?.message?.replace(/\?\S*/g, ''),
          recoveryAttempts: hlsRecoveryRef.current,
        });
        setPhase('stopped');
        setUnexpectedlyStopped(true);
      });
    } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      reportEngine('native');
      audio.src = hlsUrl;
      audio.volume = volumeRef.current;
      audio.load();
      if (!isIntentionallyPausedRef.current) {
        void audio.play().catch(onPlayError);
      }
    } else {
      reportEngine('unsupported');
      console.error('[HLS] HLS not supported in this browser');
      capturePlaybackStop('hls-unsupported');
      setPhase('stopped');
      setUnexpectedlyStopped(true);
    }
  }, [token, stopAndExpire, capturePlaybackStop]);

  // ── HLS audio element events + stall watchdog (ENG-5298) ───────────────────
  useEffect(() => {
    const audio = hlsAudioRef.current;
    if (!audio) return;

    let rafId: number | undefined;
    const onTimeUpdate = () => {
      if (audio.currentTime > 0) hasPlayedRef.current = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100 || 0);
      });
    };
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    // The sliding-window stream is endless while the pipeline runs, so 'ended'
    // means the buffer ran dry and playback fell off the tail — a real stop.
    // Skip if a stop was already recorded (e.g. session-lost, then the residual
    // buffer drains to 'ended') so one incident isn't double-counted.
    const onEnded = () => {
      setIsPlaying(false);
      if (unexpectedlyStoppedRef.current) return;
      setPhase('stopped');
      setUnexpectedlyStopped(true);
      capturePlaybackStop('stream-ended');
    };

    // Watchdog: re-checks every STALL_TICK_MS while stalled. Warns once the
    // silence is real (>= STALL_WARN_MS), keeps nudging hls.js/audio, and
    // escalates to the manual-restart overlay at STALL_ESCALATE_MS.
    const onStallTimeout = () => {
      stallWatchdogRef.current = null;
      if (
        stallStartRef.current == null ||
        isIntentionallyPausedRef.current ||
        unexpectedlyStoppedRef.current
      ) {
        return;
      }
      // Recovered between ticks — stop here (the 'playing' event usually clears
      // first, but this guards the case where it didn't fire).
      if (!audio.paused && audio.readyState >= 3 /* HAVE_FUTURE_DATA */) {
        clearStall();
        return;
      }
      const stalledMs = Date.now() - stallStartRef.current;
      // A stall before playback ever started (currentTime still 0) is usually the
      // init→first-playlist race, recoverable once the playlist reloads → longer
      // grace + forced reload, and a distinct stop reason for triage.
      const startup = !hasPlayedRef.current;
      const escalateMs = startup ? STALL_STARTUP_ESCALATE_MS : STALL_ESCALATE_MS;
      // Audible silence for BGM — count it as soon as it's clearly a stall, even
      // if it recovers a moment later. Once per stall episode.
      if (stalledMs >= STALL_WARN_MS && !stallWarnedRef.current) {
        stallWarnedRef.current = true;
        capturePlaybackDegraded('stall', {
          stalledMs,
          startup,
          bufferAhead: getBufferAhead(audio),
          currentTime: audio.currentTime,
        });
      }
      if (stalledMs >= escalateMs) {
        capturePlaybackStop(startup ? 'startup-stall' : 'stall-unrecovered', { stalledMs });
        setPhase('stopped');
        setUnexpectedlyStopped(true);
        clearStall();
        return;
      }
      // Still within the recovery window — nudge and keep watching. On a startup
      // stall force a full playlist reload (the first playlist may have had no
      // segments); mid-stream a startLoad is enough.
      try {
        const hls = hlsInstanceRef.current;
        if (hls) {
          if (startup) hls.stopLoad();
          hls.startLoad();
        } else if (startup) {
          audio.load(); // native path: re-fetch the playlist
        }
      } catch {
        // startLoad/stopLoad throw if hls was torn down — ignore
      }
      void audio.play().catch(() => null);
      stallWatchdogRef.current = setTimeout(onStallTimeout, STALL_TICK_MS);
    };

    // 'waiting'/'stalled' = buffer underrun. Record it (with buffer depth) and
    // arm the watchdog; the player keeps the hls.js instance, so most underruns
    // self-heal without the store ever touching the screen.
    const onWaiting = () => {
      if (
        phaseRef.current !== 'playing' ||
        isIntentionallyPausedRef.current ||
        unexpectedlyStoppedRef.current
      ) {
        return;
      }
      if (stallStartRef.current == null) {
        stallStartRef.current = Date.now();
        Sentry.addBreadcrumb({
          category: 'playback',
          level: 'warning',
          message: 'audio stalled (buffer underrun)',
          data: { bufferAhead: getBufferAhead(audio), currentTime: audio.currentTime },
        });
      }
      if (!stallWatchdogRef.current) {
        stallWatchdogRef.current = setTimeout(onStallTimeout, STALL_WARN_MS);
      }
    };
    // Let the hls.js bufferStalledError handler (in loadHLS) start the same
    // watchdog — the media 'waiting' event may not fire reliably at the live
    // edge / on the native path.
    beginStallRef.current = onWaiting;

    // 'playing' fires when audio actually resumes — confirms recovery, so reset
    // the stall + HLS-recovery bookkeeping and record time-to-recover.
    const onPlaying = () => {
      setIsPlaying(true);
      hlsRecoveryRef.current = 0;
      // Real playback started — an autoplay block (if any) is now lifted, so drop
      // the tap-to-resume overlay it raised.
      if (autoplayBlockedRef.current) {
        autoplayBlockedRef.current = false;
        setAutoplayBlocked(false);
      }
      // If a stop overlay was showing but the stream self-recovered (e.g. a
      // stall escalated at 12s, then the extend landed and playback resumed),
      // dismiss it — don't leave "tap to restart" sitting over playing audio.
      if (unexpectedlyStoppedRef.current) {
        setUnexpectedlyStopped(false);
        if (phaseRef.current === 'stopped') setPhase('playing');
      }
      if (stallStartRef.current != null) {
        const recoveredMs = Date.now() - stallStartRef.current;
        Sentry.addBreadcrumb({
          category: 'playback',
          message: `playback recovered after ${recoveredMs}ms`,
          data: { recoveredMs },
        });
      }
      clearStall();
    };

    // Native Safari HLS path only (audio.src = m3u8, no hls.js). A hard load
    // failure there — e.g. a live.m3u8 5xx from a D1 spike, a decode error, a
    // dropped connection — fires the media element's 'error' event, which the
    // hls.js ERROR handler (in loadHLS) never sees. Without this, hard errors on
    // iPads (the native-path devices) are a silent stop with no Sentry signal.
    const onMediaError = () => {
      // hls.js owns errors on its path; bail there.
      if (hlsInstanceRef.current) return;
      // Ignore teardown (we set audio.src = '' on stop, which fires 'error') and
      // any non-playing state.
      if (
        !audio.src ||
        isIntentionallyPausedRef.current ||
        unexpectedlyStoppedRef.current ||
        phaseRef.current !== 'playing'
      ) {
        return;
      }
      const code = audio.error?.code ?? 0; // 1 ABORTED, 2 NETWORK, 3 DECODE, 4 SRC_UNSUPPORTED
      // code 1 (MEDIA_ERR_ABORTED) is benign on iOS — the OS aborts the media
      // fetch on background/foreground cycles (and on a src swap). Escalating
      // would false-overlay on every lock; the visibility nudge + the 5s
      // tryResume interval bring it back. Breadcrumb only.
      if (code === 1) {
        Sentry.addBreadcrumb({
          category: 'playback',
          level: 'info',
          message: 'media aborted (code 1) — ignored',
        });
        return;
      }
      // Bounded in-place recovery for network/decode errors: re-fetch the
      // playlist (native has no hls.startLoad). Reuses the shared retry counter
      // (reset on 'playing').
      if ((code === 2 || code === 3) && hlsRecoveryRef.current < HLS_MAX_NETWORK_RETRIES) {
        hlsRecoveryRef.current++;
        console.warn(`[HLS] native media error code=${code} — recovery ${hlsRecoveryRef.current}`);
        Sentry.addBreadcrumb({
          category: 'playback',
          level: 'warning',
          message: `native media error recovery ${hlsRecoveryRef.current}`,
          data: { code },
        });
        // Track + run-guard the retry like the hls.js path: a Restart within the
        // window bumps pipelineRunIdRef and clears hlsRetryTimerRef, so this stale
        // timer must no-op rather than reload the brand-new session's stream.
        const scheduledRunId = pipelineRunIdRef.current;
        if (hlsRetryTimerRef.current) clearTimeout(hlsRetryTimerRef.current);
        hlsRetryTimerRef.current = setTimeout(() => {
          hlsRetryTimerRef.current = null;
          if (
            pipelineRunIdRef.current !== scheduledRunId ||
            hlsInstanceRef.current ||
            unexpectedlyStoppedRef.current
          ) {
            return;
          }
          audio.load();
          void audio.play().catch(() => null);
        }, HLS_RETRY_DELAY_MS);
        return;
      }
      // Out of retries (or non-recoverable) — alert + overlay.
      capturePlaybackStop('media-error', { code, message: audio.error?.message });
      setPhase('stopped');
      setUnexpectedlyStopped(true);
    };

    // Periodic buffer-depth breadcrumb — gives any later stop event the buffer
    // trajectory that led up to it ("track a bit more like a buffer").
    const bufferSampler = setInterval(() => {
      if (phaseRef.current !== 'playing' || audio.paused) return;
      Sentry.addBreadcrumb({
        category: 'playback',
        level: 'info',
        message: 'buffer sample',
        data: {
          bufferAhead: Math.round(getBufferAhead(audio)),
          currentTime: Math.round(audio.currentTime),
        },
      });
    }, BUFFER_SAMPLE_MS);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onWaiting);
    audio.addEventListener('error', onMediaError);
    audio.addEventListener('ended', onEnded);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      clearInterval(bufferSampler);
      clearStall();
      beginStallRef.current = () => {};
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onWaiting);
      audio.removeEventListener('error', onMediaError);
      audio.removeEventListener('ended', onEnded);
    };
  }, [loading, capturePlaybackStop, capturePlaybackDegraded, clearStall]);

  // Volume sync
  useEffect(() => {
    const audio = hlsAudioRef.current;
    if (audio) audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    const audio = hlsAudioRef.current;
    if (audio) audio.muted = muted;
  }, [muted]);

  // ── Main session flow ─────────────────────────────────────────────────────

  const startSession = useCallback(async () => {
    if (!token || !sessionTokenRef.current) return;
    isIntentionallyPausedRef.current = false;

    // Unlock the audio context while still inside the user gesture.  Without
    // this, browsers block audio.play() when it is called later from inside an
    // async callback (MANIFEST_PARSED), because the original click gesture has
    // already expired.
    {
      const audioEl = hlsAudioRef.current;
      if (audioEl) {
        audioEl.muted = true;
        void audioEl.play().catch(() => null);
        audioEl.pause();
        audioEl.muted = false;
      }
    }

    // Cancel any running pipeline
    pipelineRunIdRef.current++;
    const runId = pipelineRunIdRef.current;

    setPhase('starting');
    setTracks([]);
    tracksRef.current = [];
    setUnexpectedlyStopped(false);
    setAutoplayBlocked(false);
    autoplayBlockedRef.current = false;
    setRestartError(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    hlsRecoveryRef.current = 0;
    pipelineFailRef.current = 0;
    hasPlayedRef.current = false;
    clearStall();
    if (hlsRetryTimerRef.current) {
      clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }

    // Cancel any in-flight HLS poll fetch
    hlsPollAbortRef.current?.abort();
    hlsPollAbortRef.current = null;

    // Destroy existing HLS
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.detachMedia();
      hlsInstanceRef.current.removeAllListeners();
      hlsInstanceRef.current.destroy();
    }
    hlsInstanceRef.current = null;
    const audio = hlsAudioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }

    try {
      // Use pre-generated tracks if they exist for the current tag selection,
      // otherwise fall back to generating fresh tracks.
      let trackA: Track, trackB: Track;
      let usedShortTracks = false;
      const currentTagKey = getTagKey(
        selectedMoodRef.current,
        selectedGenresRef.current,
        melodyRef.current,
      );
      if (preGenTagKeyRef.current === currentTagKey && preGenPromiseRef.current) {
        const preGenPromise = preGenPromiseRef.current;
        const preGenLength = preGenTrackLengthRef.current;
        preGenPromiseRef.current = null;
        preGenTagKeyRef.current = '';
        preGenTrackLengthRef.current = 180;
        try {
          [trackA, trackB] = await preGenPromise;
          usedShortTracks = preGenLength < 180;
        } catch {
          // Pre-gen was cancelled or failed; generate fresh (full-length)
          const isCancelled = () => pipelineRunIdRef.current !== runId;
          [trackA, trackB] = await Promise.all([
            generateTrack(isCancelled),
            generateTrack(isCancelled),
          ]);
        }
      } else {
        // Cancel any running pre-gen since we're generating our own tracks
        preGenRunIdRef.current++;
        preGenPromiseRef.current = null;
        preGenTrackLengthRef.current = 180;
        const isCancelled = () => pipelineRunIdRef.current !== runId;
        [trackA, trackB] = await Promise.all([
          generateTrack(isCancelled),
          generateTrack(isCancelled),
        ]);
      }
      if (pipelineRunIdRef.current !== runId) return;

      setTracks([trackA, trackB]);
      tracksRef.current = [trackA, trackB];

      // Determine crossfade duration: 0 when crossfade is disabled, shorter when
      // using pre-gen short tracks so the seam stays seamless.
      const initCrossfade = crossfadeEnabledRef.current
        ? usedShortTracks
          ? PRE_GEN_CROSSFADE
          : 15
        : 0;
      firstExtendCrossfadeRef.current = initCrossfade;

      // Submit init job to ffmpeg-worker via the API
      const initJobId = `init-${Date.now()}`;
      const initRes = await fetch(`${API_BASE}/api/play/${token}/hls/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionTokenRef.current!,
        },
        body: JSON.stringify({
          jobId: initJobId,
          trackAUrl: trackA.url,
          trackBUrl: trackB.url,
          crossfadeDuration: initCrossfade,
        }),
      });
      if (!initRes.ok) throw new Error(`HLS init request failed: ${initRes.status}`);
      if (pipelineRunIdRef.current !== runId) return;

      // Wait for the crossfade + segmentation to complete
      await pollHlsJob(initJobId, runId);
      if (pipelineRunIdRef.current !== runId) return;

      // Start playing
      setPhase('playing');
      loadHLS();

      // Kick off the self-perpetuating extend pipeline
      void runPipeline(runId);
    } catch (e) {
      console.error('[Player] startSession failed:', e);
      if (pipelineRunIdRef.current === runId) {
        setPhase('idle');
        showError(t('generationError'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, generateTrack, pollHlsJob, loadHLS]);

  // Extend pipeline: while the current runId is active, keep generating tracks and
  // extending the HLS stream so playback never reaches the end.
  const runPipeline = useCallback(
    async (runId: number) => {
      if (!token) return;
      setIsPipelineRunning(true);
      let isFirstExtend = true;

      while (pipelineRunIdRef.current === runId) {
        // Only extend while actively playing. When the player is stopped/stalled
        // (e.g. a mid-playback network drop that surfaces the "tap to resume"
        // overlay) or intentionally paused, audio.currentTime is frozen, so the
        // buffer-depth throttle below can never engage — `remaining` stays
        // constant and never crosses the 5-min threshold. Each iteration would
        // otherwise fire POST /hls/extend (one ffmpeg run apiece) as fast as the
        // backend answers, producing the observed reconnect request storm. Wait
        // and re-check instead of extending.
        if (
          unexpectedlyStoppedRef.current ||
          isIntentionallyPausedRef.current ||
          phaseRef.current !== 'playing'
        ) {
          await new Promise<void>((r) => setTimeout(r, 5_000));
          continue;
        }

        // Throttle: don't extend when the buffer is already deep.
        // audio.duration reflects total playlist length loaded by hls.js.
        const audio = hlsAudioRef.current;
        if (audio) {
          const remaining = audio.duration - audio.currentTime;
          if (Number.isFinite(remaining) && remaining > 20 * 60) {
            // Sleep until remaining drops to ~17 minutes, then re-check. This floor
            // is coupled to the throttle above: kept ~3 min below it so the buffer
            // doesn't drain back toward 3 min before the next extend lands.
            // Trade-off: a deeper lookahead gives more iOS background-playback
            // safety — Safari can suspend JS for minutes when backgrounded — but
            // pre-buffering this much increases ffmpeg-worker load and R2 costs
            // (ADR-007 Stage 1a accepts +4–8% generation volume).
            const sleepMs = Math.max(5_000, (remaining - 17 * 60) * 1000);
            await new Promise<void>((r) => setTimeout(r, sleepMs));
            continue;
          }
          if (!Number.isFinite(remaining)) {
            // Buffer depth unknown (audio.duration is NaN before MANIFEST_PARSED).
            // Extending here would fall through to an immediate ffmpeg run against
            // an unknown buffer, so wait for the manifest to parse and re-check.
            await new Promise<void>((r) => setTimeout(r, 5_000));
            continue;
          }
        }

        try {
          if (!sessionTokenRef.current) break;

          // 'degraded': an extend generation timing out does NOT stop playback
          // (the buffer covers it and we retry below), so it's a warning, not a
          // "playback stopped" page — but it's still recorded per occurrence with
          // the task id, so if a near-tail buffer later drains into a stall the
          // root-cause generation timeout isn't lost (Greptile, PR #170).
          const nextTrack = await generateTrack(
            () => pipelineRunIdRef.current !== runId,
            180,
            'degraded',
          );
          if (pipelineRunIdRef.current !== runId) break;

          setTracks((prev) => {
            const next = [...prev, nextTrack];
            // Trim old tracks from the front to avoid unbounded growth
            return next.length > MAX_TRACKS ? next.slice(next.length - MAX_TRACKS) : next;
          });
          tracksRef.current = [...tracksRef.current, nextTrack].slice(-MAX_TRACKS);

          // The first extend after a short-track init must use the same crossfade duration
          // as init to keep the seam seamless. Subsequent extends use the standard 10s.
          // When crossfade is disabled, always send 0.
          const extCrossfade = crossfadeEnabledRef.current
            ? isFirstExtend
              ? firstExtendCrossfadeRef.current
              : 15
            : 0;

          const extJobId = `ext-${Date.now()}`;
          const extRes = await fetch(`${API_BASE}/api/play/${token}/hls/extend`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-Token': sessionTokenRef.current,
            },
            body: JSON.stringify({
              jobId: extJobId,
              nextTrackUrl: nextTrack.url,
              crossfadeDuration: extCrossfade,
            }),
          });

          if (!extRes.ok) {
            console.warn('[Pipeline] extend request failed:', extRes.status);
            notePipelineFailure('extend-request', { httpStatus: extRes.status });
            await new Promise((r) => setTimeout(r, 5000));
            continue;
          }
          isFirstExtend = false;
          if (pipelineRunIdRef.current !== runId) break;

          await pollHlsJob(extJobId, runId);
          pipelineFailRef.current = 0; // a full extend succeeded — clear the streak
        } catch (e) {
          if (pipelineRunIdRef.current !== runId) break;
          // Session lost on ffmpeg-worker (e.g. after deploy) — stop the
          // pipeline so the user can restart cleanly. This drains the buffer and
          // ends playback, so alert it as a real stop.
          if ((e as { errorCode?: string }).errorCode === 'SESSION_NOT_FOUND') {
            console.warn('[Pipeline] HLS session lost on server, stopping pipeline');
            capturePlaybackStop('session-lost');
            setPhase('stopped');
            setUnexpectedlyStopped(true);
            break;
          }
          console.error('[Pipeline] error, retrying in 5s:', e);
          notePipelineFailure('extend-job', { message: (e as Error)?.message });
          await new Promise((r) => setTimeout(r, 5000));
        }
      }

      if (pipelineRunIdRef.current === runId) {
        setIsPipelineRunning(false);
      }
    },
    [token, generateTrack, pollHlsJob, capturePlaybackStop, notePipelineFailure],
  );

  // Re-claim session then start fresh (used after unexpected stop)
  // Returns true only once the session claim has succeeded and a real restart
  // is under way; false if the claim itself failed (nothing was re-attached).
  const reclaimAndRestart = useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    setRestartError(null);
    setRestarting(true);
    try {
      const session = await playerApi.claimSession(token);
      sessionTokenRef.current = session.sessionToken;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(async () => {
        try {
          const result = await playerApi.heartbeat(token, sessionTokenRef.current!);
          if (result.evicted) stopAndExpire();
        } catch {
          // keep retrying
        }
      }, session.heartbeatInterval * 1000);
      void (async () => {
        try {
          const storeList = await playerApi.getStores(token);
          // The route token may have changed while this detached fetch was in
          // flight — a stale response must not overwrite the new store's
          // branding (page + Media Session metadata).
          if (tokenRef.current !== token) return;
          if (storeList[0]) {
            setStoreName(storeList[0].name);
            setLogoUrl(storeList[0].logoUrl);
          }
        } catch {
          // Branding re-fetch failed; cosmetic-only, playback unaffected.
        }
      })();
    } catch (e) {
      setRestarting(false);
      // Discriminate a terminal 4xx from a transient 5xx / network throw, the
      // same way the init-effect claim does (the error carries `.status` from
      // handleResponse; a pure network throw carries none).
      const status = (e as { status?: number } | undefined)?.status;
      if (!isTransientStatus(status)) {
        // Session stolen by another kiosk / bad token / single-session
        // rejection (terminal 4xx: 401/403/404/…) — terminal. Re-claiming would
        // reload-war with the device that legitimately holds the single
        // session, so stand down via the exact path heartbeat eviction uses:
        // stopAndExpire() sets sessionExpired, which the watchdog checks first
        // and disengages on (no Tier-1 hammering, no Tier-2 reload).
        stopAndExpire();
        return false;
      }
      // 5xx, a pure network throw (no status), or a transient 4xx (408/429) →
      // transient. Keep the prior behavior: surface the manual-restart error
      // and let the watchdog keep retrying Tier-1 on backoff (and eventually
      // Tier-2 reload) until the network recovers.
      setRestartError(t('restartFailed'));
      return false;
    }
    setRestarting(false);
    setUnexpectedlyStopped(false);
    await startSession();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startSession, stopAndExpire]);
  // Try to resume HLS in-place; if no HLS instance, fall back to full restart
  const tryResumeOrRestart = useCallback(async () => {
    if (restarting) return;
    const hls = hlsInstanceRef.current;
    if (hls) {
      setRestarting(true);
      hls.startLoad();
      const audio = hlsAudioRef.current;
      if (audio) {
        try {
          await audio.play();
          // Tapping "resume" is an explicit intent to play again. Clear the
          // intentional-pause flag like the play/resume handlers do — otherwise a
          // pause → HLS fatal error → resume sequence leaves it stuck true, and the
          // extend pipeline's guard (runPipeline) skips extending forever even
          // though playback is running, draining the buffer until it stops.
          isIntentionallyPausedRef.current = false;
          setUnexpectedlyStopped(false);
          setAutoplayBlocked(false);
          autoplayBlockedRef.current = false;
          setPhase('playing');
        } catch {
          // play() failed (buffer not ready yet) — keep overlay visible
        }
      }
      setRestarting(false);
      return;
    }
    await reclaimAndRestart();
  }, [reclaimAndRestart, restarting]);
  useEffect(() => {
    tryResumeOrRestartRef.current = tryResumeOrRestart;
  }, [tryResumeOrRestart]);

  // ── Auto-recovery watchdog (ENG-5468) ─────────────────────────────────────
  // Wire the current callbacks into refs so the watchdog interval below can be
  // armed once (per token) and read the latest closures without re-arming.
  useEffect(() => {
    reclaimAndRestartRef.current = reclaimAndRestart;
  }, [reclaimAndRestart]);
  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  // Single self-contained watchdog. It observes health via refs only (no state
  // deps → one stable interval for the session) and escalates:
  //   not dead  → reset episode bookkeeping; clear the reload loop-guard only
  //               after sustained healthy playback (a real recovery).
  //   dead      → Tier-1 bounded in-place re-init (reclaim+restart, no reload),
  //               then Tier-2 a single gated full reload guarded against loops.
  // It deliberately ignores the 401/session-replaced/expire path (that overlay
  // is intentional single-session enforcement, not a recoverable outage) and
  // never touches the bounded hls.js retries.
  useEffect(() => {
    if (!WATCHDOG_ENABLED || !token) return;
    const reloadKey = `${WATCHDOG_RELOAD_KEY_PREFIX}${token}`;

    const tick = () => {
      const now = Date.now();

      // Session replaced/expired or intentionally paused → stand down entirely.
      if (sessionExpiredRef.current || isIntentionallyPausedRef.current) {
        watchdogEngagedRef.current = false;
        watchdogDeadSinceRef.current = null;
        watchdogReinitRef.current = 0;
        watchdogHealthySinceRef.current = null;
        return;
      }

      // Confirmed playback → recovered. Disengage + reset, and once playback has
      // stayed healthy long enough, clear the cross-reload loop guard so a later
      // independent outage gets a fresh reload budget.
      const healthy =
        isPlayingRef.current && phaseRef.current === 'playing' && !unexpectedlyStoppedRef.current;
      if (healthy) {
        if (watchdogEngagedRef.current) {
          watchdogEngagedRef.current = false;
          Sentry.addBreadcrumb({
            category: 'playback',
            level: 'info',
            message: 'watchdog: recovered',
            data: { reinitAttempts: watchdogReinitRef.current },
          });
        }
        watchdogDeadSinceRef.current = null;
        watchdogReinitRef.current = 0;
        if (watchdogHealthySinceRef.current == null) {
          watchdogHealthySinceRef.current = now;
        } else if (now - watchdogHealthySinceRef.current >= WATCHDOG_HEALTHY_RESET_MS) {
          clearReloadMark(reloadKey);
        }
        return;
      }
      watchdogHealthySinceRef.current = null;

      // Engage the moment the terminal manual-restart overlay appears. Stays
      // engaged across the transient 'starting'/'idle' flips a re-init causes,
      // until healthy playback is confirmed above.
      if (unexpectedlyStoppedRef.current && !watchdogEngagedRef.current) {
        watchdogEngagedRef.current = true;
        watchdogDeadSinceRef.current = now;
        watchdogReinitRef.current = 0;
        watchdogLastAttemptRef.current = now; // grace before the first attempt
      }

      // Not our concern: idle-before-first-play, or a re-init already in flight.
      if (!watchdogEngagedRef.current) return;
      if (restartingRef.current || phaseRef.current === 'starting') return;

      // Only escalate from a confirmed-dead state: the manual-restart overlay is
      // up (unexpectedlyStopped), or a failed re-init dropped us back to idle. If
      // a re-init left us 'playing' but audio is still buffering, wait — the
      // existing stall/error handlers will re-raise the overlay if it truly
      // fails, rather than us interrupting a stream that's about to resume.
      if (!unexpectedlyStoppedRef.current && phaseRef.current !== 'idle') return;

      const deadMs = now - (watchdogDeadSinceRef.current ?? now);

      // Tier 1 — bounded in-place re-init, no page reload. The attempt is only
      // counted against the budget once its session claim actually succeeds; a
      // failed claim (e.g. a 5xx on a flaky network) leaves the counter
      // untouched so the backoff keeps retrying real re-attaches instead of
      // burning the budget on nothing. The `deadMs` gate below hands off to
      // Tier-2 once the outage is long enough, so this can't loop forever even
      // if the claim never succeeds.
      if (watchdogReinitRef.current < WATCHDOG_MAX_REINIT && deadMs < WATCHDOG_RELOAD_AFTER_MS) {
        if (now - watchdogLastAttemptRef.current < WATCHDOG_REINIT_BACKOFF_MS) return;
        watchdogLastAttemptRef.current = now;
        Sentry.addBreadcrumb({
          category: 'playback',
          level: 'warning',
          message: `watchdog: reinit attempt ${watchdogReinitRef.current + 1}`,
          data: { deadMs },
        });
        void reclaimAndRestartRef.current().then((reattached) => {
          if (reattached) watchdogReinitRef.current += 1;
        });
        return;
      }

      // Tier 2 — single gated full reload, only after Tier-1 is exhausted AND
      // the player has been continuously dead for a long window.
      if (deadMs >= WATCHDOG_RELOAD_AFTER_MS) {
        const mark = readReloadMark(reloadKey);
        if (mark.ts === 0 || now - mark.ts >= WATCHDOG_RELOAD_COOLDOWN_MS) {
          writeReloadMark(reloadKey, { ts: now, count: mark.count + 1 });
          Sentry.captureMessage('watchdog: escalate to reload', {
            level: 'warning',
            tags: { tokenSuffix: token?.slice(-6) ?? '' },
            extra: { deadMs, reloadCount: mark.count + 1 },
          });
          window.location.reload();
          return;
        }
        // Within the reload cooldown — a reload won't fix a permanently-dead
        // network and would start a reload-cycle. Back off and keep retrying
        // Tier-1 in place instead.
        Sentry.addBreadcrumb({
          category: 'playback',
          level: 'warning',
          message: 'watchdog: reload suppressed (loop guard cooldown)',
          data: { deadMs, sinceLastReloadMs: now - mark.ts },
        });
        watchdogReinitRef.current = 0;
        watchdogDeadSinceRef.current = now;
        watchdogLastAttemptRef.current = now;
      }
    };

    const interval = setInterval(tick, WATCHDOG_TICK_MS);
    return () => clearInterval(interval);
  }, [token]);

  // After a Tier-2 watchdog reload, resume playback automatically (the kiosk is
  // unattended). Gated strictly on a fresh reload marker WE wrote, so normal
  // fresh loads and staff-initiated reloads stay on the manual play button.
  useEffect(() => {
    if (!WATCHDOG_ENABLED || !token) return;
    if (loading || invalidToken) return;
    if (watchdogAutoStartedRef.current) return;
    const mark = readReloadMark(`${WATCHDOG_RELOAD_KEY_PREFIX}${token}`);
    if (mark.ts === 0 || Date.now() - mark.ts > WATCHDOG_RELOAD_COOLDOWN_MS) return;
    watchdogAutoStartedRef.current = true;
    Sentry.addBreadcrumb({
      category: 'playback',
      level: 'warning',
      message: 'watchdog: auto-start after reload',
      data: { reloadCount: mark.count },
    });
    void startSessionRef.current();
  }, [loading, invalidToken, token]);

  // ── Tags change: pre-generate (no restart during active playback) ──────────
  // Running pipeline picks up new tags / melody setting automatically via refs.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!sessionTokenRef.current) return;
    if (phase === 'playing' || phase === 'starting') return;
    const timer = setTimeout(() => {
      preGenerateTracks();
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMood, selectedGenres, melody]);

  // ── Playback controls ─────────────────────────────────────────────────────

  const togglePlay = () => {
    const audio = hlsAudioRef.current;
    if (!audio) return;
    if (isPlaying) {
      isIntentionallyPausedRef.current = true;
      audio.pause();
    } else {
      isIntentionallyPausedRef.current = false;
      setUnexpectedlyStopped(false);
      void audio.play().catch(console.error);
    }
  };

  // Progress bar is display-only — no seeking. The HLS sliding-window playlist
  // drops old segments, so backward seeking is not possible.

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${Math.floor(s % 60)
      .toString()
      .padStart(2, '0')}`;

  const formatTags = useCallback(
    (m: string | null, gs: string[]) =>
      [m, ...gs]
        .filter(Boolean)
        .map((tag) => t(`tagNames.${tag!}`, { defaultValue: tag! }))
        .join(' / '),
    [t],
  );

  // Estimate which track is currently audible based on playback position
  const nowPlayingIndex = Math.min(Math.floor(currentTime / TRACK_INTERVAL), tracks.length - 1);
  const nowPlayingTrack = tracks[nowPlayingIndex] ?? null;
  const nextTrackForDisplay = tracks[nowPlayingIndex + 1] ?? null;

  const canSkipNext = duration > 0 && (nowPlayingIndex + 1) * TRACK_INTERVAL < duration;

  const skipToNextTrack = () => {
    const audio = hlsAudioRef.current;
    if (!audio || !canSkipNext) return;
    audio.currentTime = (nowPlayingIndex + 1) * TRACK_INTERVAL;
  };

  const nextStatus = () => {
    if (nextTrackForDisplay) {
      return {
        info: formatTags(nextTrackForDisplay.mood, nextTrackForDisplay.genres),
        isGenerating: false,
      };
    }
    if (isPipelineRunning || phase === 'starting') {
      const tags = formatTags(selectedMood, selectedGenres) || 'BGM';
      return { info: `${tags} ${t('generating')}`, isGenerating: true };
    }
    return { info: t('waiting'), isGenerating: false };
  };

  // ── Media Session ─────────────────────────────────────────────────────────
  const mediaSessionActionsRef = useRef<{
    play: () => void;
    pause: () => void;
    nexttrack: () => void;
    seekforward: (offset: number) => void;
  }>({
    play: () => {},
    pause: () => {},
    nexttrack: () => {},
    seekforward: () => {},
  });

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.setActionHandler('play', () => mediaSessionActionsRef.current.play());
    navigator.mediaSession.setActionHandler('pause', () => mediaSessionActionsRef.current.pause());
    navigator.mediaSession.setActionHandler('nexttrack', () =>
      mediaSessionActionsRef.current.nexttrack(),
    );
    navigator.mediaSession.setActionHandler('seekforward', (details) =>
      mediaSessionActionsRef.current.seekforward(details?.seekOffset ?? 10),
    );
    // No-op: backward seek is impossible with sliding-window HLS
    navigator.mediaSession.setActionHandler('seekbackward', () => {});
    return () => {
      (['play', 'pause', 'nexttrack', 'seekforward', 'seekbackward'] as const).forEach((a) =>
        navigator.mediaSession.setActionHandler(a, null),
      );
    };
  }, []);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !nowPlayingTrack) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: formatTags(nowPlayingTrack.mood, nowPlayingTrack.genres) || storeName,
      artist: storeName,
      artwork: logoUrl ? [{ src: logoUrl, sizes: '512x512' }] : [],
    });
  }, [nowPlayingTrack, storeName, logoUrl, formatTags]);

  mediaSessionActionsRef.current = {
    play: () => {
      isIntentionallyPausedRef.current = false;
      setUnexpectedlyStopped(false);
      void hlsAudioRef.current?.play().catch(console.error);
    },
    pause: () => {
      isIntentionallyPausedRef.current = true;
      hlsAudioRef.current?.pause();
    },
    nexttrack: skipToNextTrack,
    seekforward: (offset: number) => {
      const audio = hlsAudioRef.current;
      if (audio && duration) {
        audio.currentTime = Math.min(audio.currentTime + offset, duration);
      }
    },
  };

  // ── Loading screen ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            {logoUrl ? (
              <div
                className="w-20 h-20 rounded-2xl overflow-hidden"
                style={{ border: '2px solid rgba(142,152,223,0.3)' }}
              >
                <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center pulse-glow"
                style={{ background: 'linear-gradient(135deg, #29ABE2, #8E98DF)' }}
              >
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                </svg>
              </div>
            )}
          </div>
          <p className="text-gray-400 text-sm mb-8">{t('subtitle')}</p>
          <div className="flex justify-center">
            <svg className="w-8 h-8 animate-spin text-[#29ABE2]" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // ── Session expired ───────────────────────────────────────────────────────
  if (sessionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'rgba(239,68,68,0.15)',
                border: '2px solid rgba(239,68,68,0.4)',
              }}
            >
              <svg
                className="w-10 h-10 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
          </div>
          <p className="text-gray-300 mb-6">{t('sessionExpired')}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 rounded-lg text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #29ABE2, #8E98DF)' }}
          >
            {t('back')}
          </button>
        </div>
      </div>
    );
  }

  // ── Invalid token ─────────────────────────────────────────────────────────
  if (invalidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card rounded-2xl p-8 w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'rgba(239,68,68,0.15)',
                border: '2px solid rgba(239,68,68,0.4)',
              }}
            >
              <svg
                className="w-10 h-10 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <p className="text-gray-300">{t('invalidToken')}</p>
        </div>
      </div>
    );
  }

  // ── Main player ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col pb-[75px] md:pb-36">
      {/* Hidden HLS audio element */}
      <audio ref={hlsAudioRef} />

      {/* Header */}
      <header className="p-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <div
                className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0"
                style={{ border: '2px solid rgba(142,152,223,0.3)' }}
              >
                <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 pulse-glow"
                style={{ background: 'linear-gradient(135deg, #29ABE2, #8E98DF)' }}
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                </svg>
              </div>
            )}
            <div>
              <h1 className="text-xl md:text-base font-bold">{storeName}</h1>
              <p className="text-xs text-gray-400">
                {t('subtitle')}{' '}
                <span className="text-gray-500">
                  | Powered by{' '}
                  <a
                    href="https://soundraw.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#29ABE2] hover:text-gray-300 transition-colors"
                  >
                    SOUNDRAW
                  </a>
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <PresetSelector
              token={token!}
              selectedMood={selectedMood}
              selectedGenres={selectedGenres}
              onLoad={(mood, genres) => {
                setSelectedMood(mood);
                setSelectedGenres(genres);
              }}
              onSuccess={showSuccess}
              onError={showError}
            />
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Tag Selection */}
      <main className="flex-1 p-4">
        <div className="max-w-7xl mx-auto p-3 md:p-4">
          <TagSelector
            selectedMood={selectedMood}
            allMoods={allMoods}
            allGenres={allGenres}
            selectedGenres={selectedGenres}
            onSelectMood={setSelectedMood}
            onSelectGenre={setSelectedGenres}
            availableMoods={availableMoods}
            availableGenres={availableGenres}
          />
        </div>
      </main>

      {/* Unexpected stop overlay — also shown when autoplay was blocked, so a
          gesture-less start that the browser refused gets an explicit tap-to-resume
          affordance instead of looking idle behind the small footer play button. */}
      {(unexpectedlyStopped || autoplayBlocked) && (
        <div className="fixed inset-0 flex items-center justify-center z-40 bg-black/60 backdrop-blur-sm">
          <div
            className="rounded-2xl p-8 flex flex-col items-center gap-4 text-center max-w-sm mx-4"
            style={{ background: 'rgba(4,11,19,0.95)', border: '1px solid rgba(142,152,223,0.2)' }}
          >
            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
            </svg>
            <p className="text-white font-semibold text-lg">
              {autoplayBlocked && !unexpectedlyStopped ? t('autoplayBlocked') : t('networkError')}
            </p>
            {restartError && <p className="text-red-400 text-sm">{restartError}</p>}
            <button
              onClick={() => void tryResumeOrRestart()}
              disabled={restarting}
              className="gradient-btn px-8 py-3 rounded-full text-white font-semibold text-lg disabled:opacity-50 flex items-center gap-2"
            >
              {restarting && (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {t('tapToRestart')}
            </button>
          </div>
        </div>
      )}

      {trial.active && trial.ended && <TrialEndModal alreadyUsed={trial.alreadyUsed} />}

      {/* Footer Player */}
      <footer
        className="fixed bottom-0 left-0 right-0 py-2 px-3 md:py-10 md:px-4"
        style={{
          background: 'rgba(4,11,19,0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(142,152,223,0.2)',
        }}
      >
        {/* Mobile layout */}
        <div className="flex flex-col md:hidden gap-2">
          {/* Row 1: Track info */}
          <div className="flex items-center justify-center gap-6">
            <div className="min-w-0 text-center">
              <p className="text-xs text-gray-500">{t('nowPlaying')}:</p>
              <p className="text-sm font-medium truncate">
                {nowPlayingTrack ? formatTags(nowPlayingTrack.mood, nowPlayingTrack.genres) : '-'}
              </p>
            </div>
            <div className="min-w-0 text-center">
              <p className="text-xs text-gray-500 flex items-center gap-1 justify-center">
                {t('nextTrack')}:
                {nextStatus().isGenerating && (
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
              </p>
              <p className="text-sm font-medium truncate text-gray-400">{nextStatus().info}</p>
            </div>
          </div>

          {/* Row 2: Controls */}
          <div className="grid grid-cols-3 items-center">
            {/* Settings gear — bare icon styled to match skip button */}
            <button
              onClick={() => setSettingsOpen(true)}
              aria-label={t('settings')}
              title={t('settings')}
              className="justify-self-center text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
              </svg>
            </button>
            {/* Play/Pause — centered in middle column */}
            <button
              onClick={
                phase === 'idle' || phase === 'stopped' ? () => void startSession() : togglePlay
              }
              disabled={phase === 'starting'}
              className="justify-self-center w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #FFFB93, #29ABE2)' }}
            >
              {phase === 'starting' ? (
                <svg
                  className="w-5 h-5 animate-spin text-[#040B13]"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : isPlaying ? (
                <svg className="w-5 h-5 text-[#040B13]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-[#040B13]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            {/* Skip to next track */}
            {phase === 'playing' ? (
              <button
                onClick={skipToNextTrack}
                disabled={!canSkipNext}
                className={`justify-self-center transition-colors ${canSkipNext ? 'text-gray-400 hover:text-white' : 'text-gray-600 opacity-50'}`}
                title={t('skipToNext', { defaultValue: 'Skip to next track' })}
                aria-label={t('skipToNext', { defaultValue: 'Skip to next track' })}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                </svg>
              </button>
            ) : (
              <div />
            )}
          </div>

          {/* Row 3: Progress */}
          {phase === 'playing' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8 text-right">
                {formatTime(currentTime)}
              </span>
              <div
                className="flex-1 h-1 rounded-full"
                style={{ background: 'rgba(142,152,223,0.2)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #29ABE2, #8E98DF)',
                  }}
                />
              </div>
              <span className="text-xs text-gray-500 w-8">{formatTime(duration)}</span>
            </div>
          )}
        </div>

        {/* Desktop layout */}
        <div className="hidden md:block">
          <div className="grid items-center px-4" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
            {/* Left — Track info */}
            <div className="min-w-0 overflow-hidden">
              <div className="flex items-center gap-6">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">{t('nowPlaying')}:</p>
                  <p className="text-sm font-medium truncate">
                    {nowPlayingTrack
                      ? formatTags(nowPlayingTrack.mood, nowPlayingTrack.genres)
                      : '-'}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    {t('nextTrack')}:
                    {nextStatus().isGenerating && (
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                    )}
                  </p>
                  <p
                    className={`text-sm truncate ${nextStatus().isGenerating ? 'text-[#FFFB93]' : 'font-medium'}`}
                  >
                    {nextStatus().info}
                  </p>
                </div>
              </div>
            </div>

            {/* Center — Play button + progress bar */}
            <div className="flex items-center gap-4 px-4">
              {/* Play / Pause */}
              <button
                data-testid="player-main-btn-desktop"
                onClick={
                  phase === 'idle' || phase === 'stopped' ? () => void startSession() : togglePlay
                }
                disabled={phase === 'starting'}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-transform ${phase === 'starting' ? 'opacity-50' : 'hover:scale-105'}`}
                style={{ background: 'linear-gradient(135deg, #FFFB93, #F2BBB7)' }}
              >
                {phase === 'starting' ? (
                  <svg className="w-5 h-5 text-black animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : isPlaying ? (
                  <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-black ml-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Skip to next track */}
              {phase === 'playing' && (
                <button
                  onClick={skipToNextTrack}
                  disabled={!canSkipNext}
                  className={`transition-colors ${canSkipNext ? 'text-gray-400 hover:text-white' : 'text-gray-600 opacity-50'}`}
                  title={t('skipToNext', { defaultValue: 'Skip to next track' })}
                  aria-label={t('skipToNext', { defaultValue: 'Skip to next track' })}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                  </svg>
                </button>
              )}

              {/* Progress bar */}
              <div className="w-80 flex items-center gap-2">
                <span className="text-xs text-gray-400 w-10 text-right">
                  {formatTime(currentTime)}
                </span>
                <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #29ABE2, #8E98DF)',
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-10">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Right — Settings gear + Volume */}
            <div className="flex items-center gap-3 justify-end">
              {/* Settings gear */}
              <button
                onClick={() => setSettingsOpen(true)}
                aria-label={t('settings')}
                title={t('settings')}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                </svg>
              </button>
              <div className="w-px h-4 bg-white/10" />
              {isIOS ? (
                <button
                  onClick={() => setMuted((m) => !m)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                >
                  <svg className="w-4 h-4 text-[#8E98DF]" fill="currentColor" viewBox="0 0 24 24">
                    {muted ? (
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                    ) : (
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                    )}
                  </svg>
                </button>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 text-[#8E98DF] flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                  </svg>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-28 h-1 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #29ABE2 0%, #8E98DF ${volume * 100}%, rgba(255,255,255,0.2) ${volume * 100}%, rgba(255,255,255,0.2) 100%)`,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </footer>

      <SettingsModal
        open={settingsOpen}
        onClose={handleSettingsClose}
        crossfadeEnabled={crossfadeEnabled}
        onCrossfadeChange={setCrossfadeEnabled}
        melodyEnabled={melody}
        onMelodyChange={setMelody}
      />
    </div>
  );
}
